/* eslint-disable react/jsx-pascal-case */
import { useContext, useEffect, useState } from "react";
import GeneralContext from "../../../context/GeneralContext/GeneralContext";
import api from "../../../services/api";
import { displayMessage } from "../../../helpers/messages";
import SL_Button from "../../../components/buttons/Button";
import { SL_Input } from "../../../components/input/Input";
import { Form, Modal, Skeleton, Tabs } from "antd";
import { SL_Select } from "../../../components/selects/Select";
import { GridFooter } from "@mui/x-data-grid-pro";
import CustomToolbar from "../../../components/grid/CustomToolbar";
import { SL_Grid } from "../../../components/grid/SL_Grid";
import gym_exercise_grid_columns from "./grid_columns/gym_exercise_grid_columns";
import {
	DeleteOutlined,
	PlusCircleOutlined,
	EditOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import { CreateGymExerciseModal } from "./CreateGymExerciseModal";
import { DetailGymExerciseModal } from "./DetailGymExerciseModal";

// Opções de gênero
const GENDER_OPTIONS = [
	{ value: "male", label: displayMessage("MALE") },
	{ value: "female", label: displayMessage("FEMALE") },
	// { value: "unspecified", label: displayMessage("UNSPECIFIED") },
];

// Opções de frequência semanal
const FREQUENCY_OPTIONS = [
	{ value: 1, label: "1x por semana" },
	{ value: 2, label: "2x por semana" },
	{ value: 3, label: "3x por semana" },
	{ value: 4, label: "4x por semana" },
	{ value: 5, label: "5x por semana" },
	{ value: 6, label: "6x por semana" },
	{ value: 7, label: "7x por semana" },
];

// Opções de dor no corpo
const BODY_PAIN_OPTIONS = [
	{ value: 'none', label: 'Não sinto dores' },
	{ value: 'lower_back', label: 'Na lombar' },
	{ value: 'shoulder', label: 'No ombro' },
	{ value: 'knee', label: 'No joelho' },
];

// Opções de duração do treino (em minutos)
const DURATION_OPTIONS = [
	{ value: 40, label: '40 min' },
	{ value: 60, label: '60 min' },
	{ value: 90, label: '90 min' },
];

// Opções de músculo foco (target_muscle). NULL/vazio = "Nenhum (genérico)" —
// aparece para qualquer foco escolhido pelo usuário (fallback).
const TARGET_MUSCLE_OPTIONS = [
	{ value: 'none', label: 'Nenhum (genérico)' },
	{ value: 'legs', label: 'Pernas' },
	{ value: 'glutes', label: 'Glúteos' },
	{ value: 'chest', label: 'Peito' },
	{ value: 'back', label: 'Costas' },
	{ value: 'shoulders', label: 'Ombros' },
	{ value: 'arms', label: 'Braços' },
];



export function DetailGymWorkoutModal({ onCloseModal, workoutId }) {
	const [modal, contextHolder] = Modal.useModal();
	const [isLoading, setIsLoading] = useState(true);
	const [workout, setWorkout] = useState(null);
	const [exercises, setExercises] = useState([]);
	const [currentExercise, setCurrentExercise] = useState(null);
	const [isCreateExerciseOpen, setIsCreateExerciseOpen] = useState(false);
	const [isDetailExerciseOpen, setIsDetailExerciseOpen] = useState(false);
	const [form] = Form.useForm();

	const { setShowLoading, mobile_mode } = useContext(GeneralContext);

	useEffect(() => {
		loadWorkout();
	}, [workoutId]);

	async function loadWorkout() {
		setIsLoading(true);
		try {
			const response = await api.gymWorkouts.find(workoutId);
			const workoutData = response.data;
			setWorkout(workoutData);
			setExercises(workoutData.exercises || []);

			form.setFieldsValue({
				name: workoutData.name,
				description: workoutData.description,
				gender: workoutData.gender,
				weekly_frequency: workoutData.weekly_frequency,
				duration_minutes: workoutData.duration_minutes,
				order_index: workoutData.order_index,
				body_pain: workoutData.body_pain,
				sequence: workoutData.sequence ?? 1,
				target_muscle: workoutData.target_muscle || 'none',
			});
		} catch (error) {
			console.error("Erro ao carregar treino:", error);
		} finally {
			setIsLoading(false);
		}
	}

	async function onFinishForm(values) {
		try {
			setShowLoading(true);
			await api.gymWorkouts.update(workoutId, values);
			await onCloseModal(true);
		} catch (error) {
			console.error("Erro ao atualizar treino:", error);
		} finally {
			setShowLoading(false);
		}
	}

	function handleExerciseRowChange(e) {
		setCurrentExercise(e?.row);
	}

	function onClickRemoveExercise() {
		modal.confirm({
			centered: true,
			content: (
				<div className="text-base w-full text-fit-red-500 text-center sm:text-start">
					{displayMessage("CONFIRM_REMOVE_EXERCISE")}
				</div>
			),
			cancelText: displayMessage("CANCEL"),
			okText: displayMessage("CONFIRM"),
			okButtonProps: { danger: true },
			onOk: removeExercise,
			icon: mobile_mode ? null : <WarningOutlined style={{ color: "red" }} />,
		});
	}

	async function removeExercise() {
		try {
			setShowLoading(true);
			await api.gymExercises.delete(currentExercise.id);
			setCurrentExercise(null);
			await loadWorkout();
		} finally {
			setShowLoading(false);
		}
	}

	async function onCloseCreateExercise(reload = false) {
		if (reload) await loadWorkout();
		setIsCreateExerciseOpen(false);
	}

	async function onCloseDetailExercise(reload = false) {
		if (reload) await loadWorkout();
		setIsDetailExerciseOpen(false);
	}

	const tabItems = [
		{
			key: "1",
			label: displayMessage("EDIT_WORKOUT"),
			children: (
				<Form
					onFinish={onFinishForm}
					layout="vertical"
					form={form}
					className={`flex gap-2 flex-col`}
				>
					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								{displayMessage("WORKOUT_NAME")}:
							</span>
						}
						name="name"
						className={`w-full m-0`}
						required
						rules={[
							{ required: true, message: displayMessage("NAME_MANDATORY") },
						]}
					>
						<SL_Input type="text" className="text-blue-700" />
					</Form.Item>

					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								{displayMessage("DESCRIPTION")}:
							</span>
						}
						name="description"
						className={`w-full m-0`}
					>
						<SL_Input textarea={true} rows={2} className="text-blue-700" />
					</Form.Item>

					<div className="gap-4 flex w-full flex-col smb:flex-row mt-2">
						<Form.Item
							label={
								<span className="text-blue-700 font-semibold">
									{displayMessage("GENDER")}:
								</span>
							}
							name="gender"
							className={`w-full m-0`}
							required
						>
							<SL_Select options={GENDER_OPTIONS} />
						</Form.Item>

						<Form.Item
							label={
								<span className="text-blue-700 font-semibold">
									{displayMessage("WEEKLY_FREQUENCY")}:
								</span>
							}
							name="weekly_frequency"
							className={`w-full m-0`}
							required
						>
							<SL_Select options={FREQUENCY_OPTIONS} />
						</Form.Item>
					</div>

					<div className="gap-4 flex w-full flex-col smb:flex-row mt-2">
						<Form.Item
							label={
								<span className="text-blue-700 font-semibold">
									Dor no corpo:
								</span>
							}
							name="body_pain"
							className={`w-full m-0`}
						>
							<SL_Select options={BODY_PAIN_OPTIONS} />
						</Form.Item>
					</div>

					<div className="gap-4 flex w-full flex-col smb:flex-row mt-2">
						<Form.Item
							label={
								<span className="text-blue-700 font-semibold">
									{displayMessage("DURATION_MINUTES")}:
								</span>
							}
							name="duration_minutes"
							className={`w-full m-0`}
						>
							<SL_Select options={DURATION_OPTIONS} />
						</Form.Item>

						<Form.Item
							label={
								<span className="text-blue-700 font-semibold">
									{displayMessage("ORDER_INDEX")}:
								</span>
							}
							name="order_index"
							className={`w-full m-0`}
						>
							<SL_Input type="number" className="text-blue-700" />
						</Form.Item>
					</div>

					<div className="gap-4 flex w-full flex-col smb:flex-row mt-2">
						<Form.Item
							label={
								<span className="text-blue-700 font-semibold">
									{displayMessage("SEQUENCE")}:
								</span>
							}
							name="sequence"
							className={`w-full m-0`}
							required
							rules={[
								{ required: true, message: displayMessage("SEQUENCE_MANDATORY") },
							]}
							tooltip={displayMessage("SEQUENCE_HELPER")}
						>
							<SL_Input type="number" className="text-blue-700" />
						</Form.Item>
					</div>

					<div className="gap-4 flex w-full flex-col smb:flex-row mt-2">
						<Form.Item
							label={
								<span className="text-blue-700 font-semibold">
									Músculo foco:
								</span>
							}
							name="target_muscle"
							className={`w-full m-0`}
							tooltip='Deixe em "Nenhum (genérico)" para que o treino apareça para qualquer foco escolhido pelo usuário. Defina um músculo para criar uma variação que substitui o genérico de mesma posição no split.'
						>
							<SL_Select options={TARGET_MUSCLE_OPTIONS} />
						</Form.Item>
					</div>
				</Form>
			),
		},
		{
			key: "2",
			label: `${displayMessage("EXERCISES")} (${exercises.length})`,
			children: (
				<div className="flex flex-col gap-2">
					<div className="flex gap-2 flex-wrap">
						<SL_Button
							type="default"
							className="flex items-center"
							onClick={() => setIsCreateExerciseOpen(true)}
							icon={<PlusCircleOutlined />}
						>
							{displayMessage("ADD_EXERCISE")}
						</SL_Button>
						<SL_Button
							type="default"
							disabled={!currentExercise}
							className="flex items-center"
							onClick={() => setIsDetailExerciseOpen(true)}
							icon={<EditOutlined />}
						>
							{displayMessage("EDIT_EXERCISE")}
						</SL_Button>
						<SL_Button
							disabled={!currentExercise}
							danger={true}
							className="flex items-center"
							onClick={onClickRemoveExercise}
							icon={<DeleteOutlined />}
						>
							{displayMessage("REMOVE_EXERCISE")}
						</SL_Button>
					</div>
					<div className="h-[300px]">
						<SL_Grid
							rows={exercises}
							columns={gym_exercise_grid_columns}
							components={{
								Toolbar: () => <CustomToolbar />,
								Footer: () => <GridFooter />,
							}}
							onRowClick={handleExerciseRowChange}
						/>
					</div>
				</div>
			),
		},
	];

	return (
		<>
			{contextHolder}
			{isCreateExerciseOpen && (
				<CreateGymExerciseModal
					onCloseModal={onCloseCreateExercise}
					workoutId={workoutId}
				/>
			)}
			{isDetailExerciseOpen && currentExercise && (
				<DetailGymExerciseModal
					onCloseModal={onCloseDetailExercise}
					exerciseId={currentExercise.id}
				/>
			)}

			<Modal
				title={
					<div className="text-xl font-bold text-blue-700 mr-3">
						{displayMessage("VIEW_WORKOUT")} - {workout?.name || "..."}
					</div>
				}
				open={true}
				onCancel={() => onCloseModal(false)}
				centered={true}
				footer={
					<div className="flex flex-col ss:flex-row gap-2 justify-end">
						<SL_Button
							className="w-full ss:w-[150px] bg-gray-500 border-transparent"
							onClick={() => onCloseModal(false)}
						>
							{displayMessage("CLOSE")}
						</SL_Button>

						<SL_Button
							className="w-full ss:w-[150px] m-0 bg-blue-600"
							onClick={() => form.submit()}
						>
							{displayMessage("SAVE")}
						</SL_Button>
					</div>
				}
				maskClosable={false}
				width={800}
			>
				{isLoading ? <Skeleton active /> : <Tabs items={tabItems} />}
			</Modal>
		</>
	);
}


